import { Component, Prop, Vue} from "vue-property-decorator";
import { ProductSummaryModuleModel, AddToCartRequestModel, Product, UiState } from "../models";
import { productConfiguratorStore as store } from "./../store";
import http from "@/services/Http";
import HtmlElementHelper from "@/services/HtmlElementHelper";
import GoogleEnhancedEcommerce from "@/services/GoogleEnhancedEcommerce";
import { IGeeProduct } from "@/models/GoogleEnhancedEcommerce";
import ProductSpecification from "./ProductSpecification";
import { EventBus } from "@/events";
import { AddToCartContainer } from "@/components";
import CarbonSavingsCalculation from "../../../components/ProductPage/CarbonSavingsCalculation";
import CarbonSavingsCompilation from "@/components/ClimateSavings/CarbonSavingsCompilation";

@Component({
    components: {
        ProductSpecification,
        AddToCartContainer,
        CarbonSavingsCalculation,
        CarbonSavingsCompilation,
  }
})
export default class ProductSummaryModule extends Vue {
    @Prop()
    protected vm!: ProductSummaryModuleModel;
    protected productStates: Array<UiState<boolean>> = [];
    protected carbonSavingsProductStates: Array<UiState<boolean>> = [];

    private addedToCartModalName: string = "addedToCartModal";
    private isLoading: boolean = false;

    protected mounted() {
        EventBus.$on("stepchanged", () =>  {
            this.productStates = [];
            this.carbonSavingsProductStates = [];
        });
    }

    protected toggleProductSpecification(product: Product) {
        const productStateIndex = this.productStates.findIndex(s => s.id === product.articleNumber);
        if (productStateIndex !== -1) {
            const productState = this.productStates[productStateIndex];
            productState.state = !productState.state;
            this.productStates.splice(productStateIndex, 1, productState);
        }
    }

    protected showProductSpecification(product: Product): boolean {
        const productState = this.getProductState(product);
        if (productState) {
            return productState.state;
        }
        return false;
    }

    protected handleQuantityUpdate(event: Event, product: Product) {
        if (event.target instanceof HTMLInputElement && !HtmlElementHelper.isValidNumberInput(event.target)) {
                product.confirmedQuantity = Number(event.target.min);
        }
    }

    protected addToCart() {
        if (this.products.length) {
            this.isLoading = true;
            const request: AddToCartRequestModel = {
                products: this.products
            };

            http.post<void>(store.apiUrl + "/addtocart", request)
            .then(() => {
                EventBus.$emit("productAddedToCartConfigurator", store.configuratorType, window.site.settings.webShopActivated === "true");
                window.Accelerator.libs.cart.updateMiniCart();
                this.reportAddToCartToGoogleEnhancedEcommerce(this.products);
            })
            .finally(() => this.isLoading = false);
        }
    }

    protected redirectToCart() {
        location.href = this.vm.cartUrl;
    }

    protected getProductState(product: Product) {
        return this.productStates.find(ps => ps.id === product.articleNumber);
    }

    protected getCarbonSavingsProductState(product: Product) {
        return this.carbonSavingsProductStates.find(ps => ps.id === product.articleNumber);
    }

    protected toggleCarbonSavingsInfo(product: Product) {
        const productStateIndex = this.carbonSavingsProductStates.findIndex(s => s.id === product.articleNumber);
        if (productStateIndex !== -1) {
            const productState = this.carbonSavingsProductStates[productStateIndex];
            productState.state = !productState.state;
            this.carbonSavingsProductStates.splice(productStateIndex, 1, productState);
        }
    }

    protected showCarbonSavingsInfo(product: Product): boolean {
        const productState = this.getCarbonSavingsProductState(product);
        if (productState) {
            return productState.state;
        }
        return false;
    }

    private reportAddToCartToGoogleEnhancedEcommerce(products: Product[]) {
        const gee = new GoogleEnhancedEcommerce();

        if (!products.some(x => x.productPrice.price > 0)) {
            http.post<Product[]>(store.apiUrl + "/getproductprice", products)
            .then(data => {
                 products = data;
            });
        }

        const googleProducts = products.map<IGeeProduct>(product => ({
            category: "",
            id: product.articleNumber,
            name: product.name,
            price: product.productPrice.price,
            quantity: product.confirmedQuantity,
            itemPriceUnit: product.itemPriceUnit
        }));

        gee.PushProductsAddToCartToGoogle(googleProducts, products[0].productPrice.currencyCode, this.configuratorIdentifier);
    }

    get addToCartDisabled() {
        return !this.products.length || this.isLoading;
    }

    get translations() {
        return store.translations;
    }

    get configuratorIdentifier() {
        return store.configuratorType;
    }

    get isProductWebShop() {
        return window.site && window.site.settings.activateProductWebshop === "true";
    }

    get carbonSavings() {
        let carbonSavings = 0;
        this.products.map(x =>  {
            if (x.carbonSavingsModel.savingsComparedToNonECOVariant > 0 && x.carbonSavingsModel.isCurrentVariantECO) {
                const calculatedQuantity = x.confirmedQuantity * x.carbonSavingsModel.packagingFactor;
                const quantityWithFactor = x.confirmedQuantity > 5 ? Math.floor(calculatedQuantity) : x.confirmedQuantity;
                carbonSavings += (x.carbonSavingsModel.globalWarmingPotentialMainProduct * quantityWithFactor -
                    x.carbonSavingsModel.globalWarmingPotentialECOProduct * x.confirmedQuantity);
            }
        });
        return carbonSavings;
    }

    get imageUrlCompilation() {
        let imagesrc = "";
        this.products.map(x =>  {
            if (x.carbonSavingsModel.imageUrl && imagesrc === "") {
                imagesrc = x.carbonSavingsModel.imageUrl;
            }
        });
        return imagesrc;
    }

    get products() {
        store.products.forEach(p => {
            if (!this.getProductState(p)) {
                this.productStates.push({ id: p.articleNumber} as UiState<boolean>);
                this.carbonSavingsProductStates.push({ id: p.articleNumber} as UiState<boolean>);
            }
        });

        return store.products.filter(x => x.added).sort((a, b) => {
            return a.name.localeCompare(b.name, "sv", { numeric: true});
          });
    }
}
